import {
  Autocomplete,
  Stack,
  TextField,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { type FC, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { useAllCodes, useMutateCreateLog, useMutateUpdateLog } from '../../api/index.js';
import { type Code, type Log, type LogMetadata } from '../../models/index.js';
import AutocompleteOptions from '../AutocompleteOptions.js';
import FormDialog from '../Forms/FormDialog.js';
import { type LogFormValues } from '../Forms/formTypes.js';
import { logFormSchema } from '../Forms/validations.js';

const defaultValues: LogFormValues = {
  createdAt: DateTime.now(),
  code: {} as Code,
  metadata: {},
};

interface LogFormDialogProps {
  log?: Log;
  open: boolean;
  onClose: () => void;
  refetch: () => void;
  patientId?: string;
}

const LogFormDialog: FC<LogFormDialogProps> = ({
  log,
  open,
  refetch,
  patientId,
  onClose,
}) => {
  const { data: codes = [] } = useAllCodes({
    includeMetadata: true,
  });
  const parseMetadata = (metadata: LogMetadata[]) => {
    const result: { [id: string]: string } = {};
    metadata.forEach((item: LogMetadata) => {
      if (item) {
        // @ts-expect-error item?.codeMetadataId may be undefined per the optional chain — runtime-checked above
        result[item?.codeMetadataId] = item.notes;
      }
    });
    return result;
  };

  const updateMutation = useMutateUpdateLog();

  const createMutation = useMutateCreateLog();
  const formattedData: LogFormValues = useMemo(() => {
    if (log && codes) {
      return {
        createdAt: DateTime.fromISO(log?.createdAt),
        code: codes.find((code) => code.id === log.code.id) || {} as Code,
        metadata: parseMetadata(log.metadata),
      };
    }
    return defaultValues;
  }, [log, codes]);

  return (
    <FormDialog
      open={open}
      name={'log'}
      onClose={onClose}
      data={formattedData}
      schema={logFormSchema}
      isEditing={!!log}
      watch={'code'}
      onSubmit={async (data, isEditing) => {
        if (!patientId) return;
        if (isEditing && log) {
          await updateMutation.mutateAsync({
            patientId,
            id: log.id,
            codeId: data.code.id,
            metadata: data.metadata,
            createdAt: data.createdAt.toJSDate(),
          });
        } else {
          await createMutation.mutateAsync({
            patientId,
            codeId: data.code?.id,
            metadata: data.metadata,
            createdAt: data.createdAt.toJSDate(),
          });
        }
      }}
      refetch={refetch}
    >
      {(control, errors, code: Code) => (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Controller
            name={'createdAt'}
            control={control}
            render={({ field }) => (
              <DateTimePicker
                {...field}
                disableFuture
                minDateTime={DateTime.now().minus({ minute: 10 * 24 * 60 })}
                slotProps={{
                  textField: {
                    InputLabelProps: { required: true },
                    helperText: errors?.createdAt?.message || 'This does not reflect the patient\'s timezone',
                    fullWidth: true,
                  },
                }}
                label={'Date and time'}
              />
            )}
          />
          <Controller
            name={'code'}
            control={control}
            render={({ field }) => {
              return (
                <Autocomplete
                  autoFocus
                  value={!_.isEmpty(field.value) ? field.value : null}
                  onChange={(_: any, value: Code | null) => field.onChange(value)}
                  id={'code-log-select'}
                  options={codes}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(code: Code) => (!_.isEmpty(code) ? `${code.code} - ${code.description}` : '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      InputLabelProps={{ required: true }}
                      label={'Code'}
                      error={!!errors?.code}
                      helperText={errors?.code?.message || ''}
                    />
                  )}
                  renderOption={({ key, ...props }, option, { inputValue }) => (
                    <AutocompleteOptions option={`${option.code} - ${option.description}`} value={inputValue} key={key} {...props} />
                  )}
                />
              );
            }}
          />
          {code?.metadata?.map((metadata) => (
            <Controller
              key={`metadata[${metadata.id}]`}
              // @ts-expect-error react-hook-form Controller `name` prop typing doesn't accept dynamic dotted paths
              name={`metadata[${metadata.id}]`}
              control={control}
              render={({ field }) => (
                <TextField {...field} multiline={metadata.inputType === 'textarea'} label={metadata.prompt} />
              )}
            />
          ))}
        </Stack>
      )}
    </FormDialog>
  );
};

export default LogFormDialog;
